import { useState, useEffect, useRef, useMemo, useCallback, type MouseEvent } from 'react';
import type { LibFilterOption } from '../library/components/LibraryFilterPopover';
import type {
  LibEmptyVariant,
  WorkoutDateFilterType,
  WorkoutDurationBucket,
  WorkoutSortColumn,
  WorkoutSortDirection,
} from '../types/library';
import {
  VALID_WORKOUT_DATE_TYPES,
  VALID_WORKOUT_DURATION_BUCKETS,
  VALID_WORKOUT_SORT_COLS,
  VALID_WORKOUT_SORT_DIRS,
} from '../types/library';
import { readWkViewState, writeWkViewState } from '../utils/libraryViewState';
import { getWorkoutExerciseCount } from '../utils/workoutLibraryRowUtils';

export const LIB_WORKOUTS_PAGE_SIZE = 15;

type LibSortKey = 'newest' | 'oldest' | 'name-asc' | 'name-desc' | 'most-ex' | 'fewest-ex';

const LIB_SORT_OPTIONS: { id: LibSortKey; label: string; column: WorkoutSortColumn; direction: WorkoutSortDirection }[] = [
  { id: 'newest', label: 'Newest first', column: 'createdAt', direction: 'desc' },
  { id: 'oldest', label: 'Oldest first', column: 'createdAt', direction: 'asc' },
  { id: 'name-asc', label: 'Name (A→Z)', column: 'name', direction: 'asc' },
  { id: 'name-desc', label: 'Name (Z→A)', column: 'name', direction: 'desc' },
  { id: 'most-ex', label: 'Most exercises', column: 'exercises', direction: 'desc' },
  { id: 'fewest-ex', label: 'Fewest exercises', column: 'exercises', direction: 'asc' },
];

export type UseLibraryWorkoutsParams = {
  workouts: any[];
};

export function useLibraryWorkouts({ workouts }: UseLibraryWorkoutsParams) {
  const [workoutSortColumn, setWorkoutSortColumn] = useState<WorkoutSortColumn>(() => {
    const saved = readWkViewState();
    return saved.sortColumn && VALID_WORKOUT_SORT_COLS.includes(saved.sortColumn) ? saved.sortColumn : 'createdAt';
  });
  const [workoutSortDirection, setWorkoutSortDirection] = useState<WorkoutSortDirection>(() => {
    const saved = readWkViewState();
    return saved.sortDirection && VALID_WORKOUT_SORT_DIRS.includes(saved.sortDirection) ? saved.sortDirection : 'desc';
  });
  const [workoutNameFilter, setWorkoutNameFilter] = useState<string>(() => {
    const saved = readWkViewState();
    return typeof saved.search === 'string' ? saved.search : '';
  });
  const [workoutDateFilter, setWorkoutDateFilter] = useState<string>(() => {
    const saved = readWkViewState();
    return typeof saved.dateFilter === 'string' ? saved.dateFilter : '';
  });
  const [workoutDateFilterType, setWorkoutDateFilterType] = useState<WorkoutDateFilterType>(() => {
    const saved = readWkViewState();
    return saved.dateFilterType && VALID_WORKOUT_DATE_TYPES.includes(saved.dateFilterType) ? saved.dateFilterType : 'exact';
  });
  const [workoutDateFilterEnd, setWorkoutDateFilterEnd] = useState<string>(() => {
    const saved = readWkViewState();
    return typeof saved.dateFilterEnd === 'string' ? saved.dateFilterEnd : '';
  });

  const [libMuscleFilter, setLibMuscleFilter] = useState<Set<string>>(() => {
    const saved = readWkViewState();
    return new Set(Array.isArray(saved.muscle) ? saved.muscle.filter((v): v is string => typeof v === 'string') : []);
  });
  const [libEquipmentFilter, setLibEquipmentFilter] = useState<Set<string>>(() => {
    const saved = readWkViewState();
    return new Set(Array.isArray(saved.equipment) ? saved.equipment.filter((v): v is string => typeof v === 'string') : []);
  });
  const [libDurationFilter, setLibDurationFilter] = useState<Set<WorkoutDurationBucket>>(() => {
    const saved = readWkViewState();
    const arr = Array.isArray(saved.durations)
      ? saved.durations.filter((v): v is WorkoutDurationBucket =>
          VALID_WORKOUT_DURATION_BUCKETS.includes(v as WorkoutDurationBucket))
      : [];
    return new Set(arr);
  });
  const [libWorkoutsPage, setLibWorkoutsPage] = useState<number>(() => {
    const saved = readWkViewState();
    return typeof saved.page === 'number' && Number.isFinite(saved.page) && saved.page >= 1 ? Math.floor(saved.page) : 1;
  });
  const [libOpenChip, setLibOpenChip] = useState<'muscle' | 'equipment' | 'duration' | 'sort' | null>(null);
  const libMuscleChipRef = useRef<HTMLDivElement>(null);
  const libEquipmentChipRef = useRef<HTMLDivElement>(null);
  const libDurationChipRef = useRef<HTMLDivElement>(null);
  const libSortChipRef = useRef<HTMLDivElement>(null);

  const handleWorkoutSort = useCallback((column: WorkoutSortColumn, e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('.filter-trigger') || target.closest('.filter-dropdown')) {
      return;
    }
    if (workoutSortColumn === column) {
      setWorkoutSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setWorkoutSortColumn(column);
      setWorkoutSortDirection('asc');
    }
  }, [workoutSortColumn]);

  const filteredAndSortedWorkouts = useMemo(() => {
    let filtered = workouts.filter((workout) => {
      if (workoutNameFilter.trim()) {
        const query = workoutNameFilter.toLowerCase();
        const name = (workout.name || '').toLowerCase();
        if (!name.includes(query)) return false;
      }

      if (libMuscleFilter.size > 0) {
        const tags = (workout.focusTags ?? []) as string[];
        if (!tags.some((t) => libMuscleFilter.has(t))) return false;
      }

      if (libEquipmentFilter.size > 0) {
        const tags = (workout.equipmentTags ?? []) as string[];
        if (!tags.some((t) => libEquipmentFilter.has(t))) return false;
      }

      if (libDurationFilter.size > 0) {
        const mins = workout.estimatedDurationMinutes;
        if (mins == null || typeof mins !== 'number') return false;
        const matchesAny =
          (libDurationFilter.has('lt30') && mins < 30)
          || (libDurationFilter.has('30to60') && mins >= 30 && mins <= 60)
          || (libDurationFilter.has('gt60') && mins > 60);
        if (!matchesAny) return false;
      }

      if (workoutDateFilter) {
        const workoutDate = workout.createdAt ? new Date(workout.createdAt) : null;
        if (!workoutDate) return false;

        const workoutDateNormalized = new Date(workoutDate.getFullYear(), workoutDate.getMonth(), workoutDate.getDate());

        if (workoutDateFilterType === 'exact') {
          const filterDate = new Date(workoutDateFilter);
          const filterDateNormalized = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
          if (workoutDateNormalized.getTime() !== filterDateNormalized.getTime()) return false;
        } else if (workoutDateFilterType === 'before') {
          const filterDate = new Date(workoutDateFilter);
          const filterDateNormalized = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
          if (workoutDateNormalized >= filterDateNormalized) return false;
        } else if (workoutDateFilterType === 'after') {
          const filterDate = new Date(workoutDateFilter);
          const filterDateNormalized = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
          if (workoutDateNormalized <= filterDateNormalized) return false;
        } else if (workoutDateFilterType === 'between') {
          if (!workoutDateFilterEnd) return false;
          const filterDateStart = new Date(workoutDateFilter);
          const filterDateStartNormalized = new Date(filterDateStart.getFullYear(), filterDateStart.getMonth(), filterDateStart.getDate());
          const filterDateEnd = new Date(workoutDateFilterEnd);
          const filterDateEndNormalized = new Date(filterDateEnd.getFullYear(), filterDateEnd.getMonth(), filterDateEnd.getDate());
          if (workoutDateNormalized < filterDateStartNormalized || workoutDateNormalized > filterDateEndNormalized) return false;
        }
      }

      return true;
    });

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (workoutSortColumn) {
        case 'name':
          comparison = (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
          break;
        case 'createdAt': {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          comparison = dateA - dateB;
          break;
        }
        case 'exercises':
          comparison = getWorkoutExerciseCount(a) - getWorkoutExerciseCount(b);
          break;
      }
      return workoutSortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [
    workouts,
    workoutSortColumn,
    workoutSortDirection,
    workoutNameFilter,
    workoutDateFilter,
    workoutDateFilterType,
    workoutDateFilterEnd,
    libMuscleFilter,
    libEquipmentFilter,
    libDurationFilter,
  ]);

  const libMuscleOptions = useMemo<string[]>(() => {
    const s = new Set<string>();
    workouts.forEach((w: any) => ((w.focusTags ?? []) as string[]).forEach((t) => t && s.add(t)));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [workouts]);

  const libEquipmentOptions = useMemo<string[]>(() => {
    const s = new Set<string>();
    workouts.forEach((w: any) => ((w.equipmentTags ?? []) as string[]).forEach((t) => t && s.add(t)));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [workouts]);

  const libCurrentSort = useMemo(() => {
    return (
      LIB_SORT_OPTIONS.find(
        (opt) => opt.column === workoutSortColumn && opt.direction === workoutSortDirection,
      ) ?? LIB_SORT_OPTIONS[0]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutSortColumn, workoutSortDirection]);

  const libSortIsDefault = libCurrentSort.id === 'newest';
  const libDurationActive = libDurationFilter.size > 0;

  const workoutPassesWithout = useCallback((
    workout: any,
    exclude: 'muscle' | 'equipment' | 'duration',
  ): boolean => {
    if (workoutNameFilter.trim()) {
      const q = workoutNameFilter.toLowerCase();
      if (!(workout.name || '').toLowerCase().includes(q)) return false;
    }
    if (exclude !== 'muscle' && libMuscleFilter.size > 0) {
      const tags = (workout.focusTags ?? []) as string[];
      if (!tags.some((t) => libMuscleFilter.has(t))) return false;
    }
    if (exclude !== 'equipment' && libEquipmentFilter.size > 0) {
      const tags = (workout.equipmentTags ?? []) as string[];
      if (!tags.some((t) => libEquipmentFilter.has(t))) return false;
    }
    if (exclude !== 'duration' && libDurationFilter.size > 0) {
      const mins = workout.estimatedDurationMinutes;
      if (mins == null || typeof mins !== 'number') return false;
      const ok =
        (libDurationFilter.has('lt30') && mins < 30)
        || (libDurationFilter.has('30to60') && mins >= 30 && mins <= 60)
        || (libDurationFilter.has('gt60') && mins > 60);
      if (!ok) return false;
    }
    return true;
  }, [workoutNameFilter, libMuscleFilter, libEquipmentFilter, libDurationFilter]);

  const libMuscleMatchCounts = useMemo(() => {
    const counts = new Map<string, number>();
    libMuscleOptions.forEach((opt) => counts.set(opt, 0));
    for (const w of workouts) {
      if (!workoutPassesWithout(w, 'muscle')) continue;
      const tags = (w.focusTags ?? []) as string[];
      const seen = new Set<string>();
      for (const t of tags) {
        if (counts.has(t) && !seen.has(t)) {
          counts.set(t, (counts.get(t) ?? 0) + 1);
          seen.add(t);
        }
      }
    }
    return counts;
  }, [workouts, libMuscleOptions, workoutPassesWithout]);

  const libEquipmentMatchCounts = useMemo(() => {
    const counts = new Map<string, number>();
    libEquipmentOptions.forEach((opt) => counts.set(opt, 0));
    for (const w of workouts) {
      if (!workoutPassesWithout(w, 'equipment')) continue;
      const tags = (w.equipmentTags ?? []) as string[];
      const seen = new Set<string>();
      for (const t of tags) {
        if (counts.has(t) && !seen.has(t)) {
          counts.set(t, (counts.get(t) ?? 0) + 1);
          seen.add(t);
        }
      }
    }
    return counts;
  }, [workouts, libEquipmentOptions, workoutPassesWithout]);

  const libDurationMatchCounts = useMemo<Record<WorkoutDurationBucket, number>>(() => {
    const out: Record<WorkoutDurationBucket, number> = { lt30: 0, '30to60': 0, gt60: 0 };
    for (const w of workouts) {
      if (!workoutPassesWithout(w, 'duration')) continue;
      const mins = w.estimatedDurationMinutes;
      if (mins == null || typeof mins !== 'number') continue;
      if (mins < 30) out.lt30++;
      else if (mins <= 60) out['30to60']++;
      else out.gt60++;
    }
    return out;
  }, [workouts, workoutPassesWithout]);

  const libWorkoutsHasActiveControls =
    libMuscleFilter.size > 0
    || libEquipmentFilter.size > 0
    || libDurationFilter.size > 0
    || workoutNameFilter.trim() !== ''
    || !libSortIsDefault
    || workoutDateFilter !== '';

  const libWorkoutsAppliedCount =
    libMuscleFilter.size
    + libEquipmentFilter.size
    + libDurationFilter.size
    + (workoutNameFilter.trim() !== '' ? 1 : 0)
    + (!libSortIsDefault ? 1 : 0);

  const handleClearAllWorkouts = useCallback(() => {
    setLibMuscleFilter(new Set());
    setLibEquipmentFilter(new Set());
    setLibDurationFilter(new Set());
    setWorkoutNameFilter('');
    setWorkoutSortColumn('createdAt');
    setWorkoutSortDirection('desc');
    setLibWorkoutsPage(1);
    setWorkoutDateFilter('');
    setWorkoutDateFilterType('exact');
    setWorkoutDateFilterEnd('');
  }, []);

  const libWorkoutsTotalPages = Math.max(1, Math.ceil(filteredAndSortedWorkouts.length / LIB_WORKOUTS_PAGE_SIZE));

  const libWorkoutsPageRows = useMemo(() => {
    const start = (libWorkoutsPage - 1) * LIB_WORKOUTS_PAGE_SIZE;
    return filteredAndSortedWorkouts.slice(start, start + LIB_WORKOUTS_PAGE_SIZE);
  }, [filteredAndSortedWorkouts, libWorkoutsPage]);

  const libRangeStart = filteredAndSortedWorkouts.length === 0 ? 0 : (libWorkoutsPage - 1) * LIB_WORKOUTS_PAGE_SIZE + 1;
  const libRangeEnd = Math.min(filteredAndSortedWorkouts.length, libWorkoutsPage * LIB_WORKOUTS_PAGE_SIZE);

  const libWkFiltersHydratedRef = useRef(false);
  useEffect(() => {
    if (!libWkFiltersHydratedRef.current) {
      libWkFiltersHydratedRef.current = true;
      return;
    }
    setLibWorkoutsPage(1);
  }, [
    workoutNameFilter,
    libMuscleFilter,
    libEquipmentFilter,
    libDurationFilter,
    workoutSortColumn,
    workoutSortDirection,
    workouts.length,
  ]);

  useEffect(() => {
    writeWkViewState({
      search: workoutNameFilter,
      muscle: Array.from(libMuscleFilter),
      equipment: Array.from(libEquipmentFilter),
      durations: Array.from(libDurationFilter),
      dateFilter: workoutDateFilter,
      dateFilterType: workoutDateFilterType,
      dateFilterEnd: workoutDateFilterEnd,
      sortColumn: workoutSortColumn,
      sortDirection: workoutSortDirection,
      page: libWorkoutsPage,
    });
  }, [
    workoutNameFilter,
    libMuscleFilter,
    libEquipmentFilter,
    libDurationFilter,
    workoutDateFilter,
    workoutDateFilterType,
    workoutDateFilterEnd,
    workoutSortColumn,
    workoutSortDirection,
    libWorkoutsPage,
  ]);

  useEffect(() => {
    if (libWorkoutsPage > libWorkoutsTotalPages) setLibWorkoutsPage(libWorkoutsTotalPages);
  }, [libWorkoutsPage, libWorkoutsTotalPages]);

  useEffect(() => {
    if (!libOpenChip) return;
    const handle = (e: Event) => {
      const target = e.target as Node;
      const refs: [typeof libOpenChip, React.RefObject<HTMLDivElement>][] = [
        ['muscle', libMuscleChipRef],
        ['equipment', libEquipmentChipRef],
        ['duration', libDurationChipRef],
        ['sort', libSortChipRef],
      ];
      for (const [name, ref] of refs) {
        if (libOpenChip === name && ref.current && !ref.current.contains(target)) {
          setLibOpenChip(null);
        }
      }
    };
    document.addEventListener('mousedown', handle);
    document.addEventListener('touchstart', handle);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('touchstart', handle);
    };
  }, [libOpenChip]);

  const libBuildPageWindow = useCallback((current: number, total: number): Array<number | 'ellipsis'> => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 3) return [1, 2, 3, 4, 'ellipsis', total];
    if (current >= total - 2) return [1, 'ellipsis', total - 3, total - 2, total - 1, total];
    return [1, 'ellipsis', current - 1, current, current + 1, 'ellipsis', total];
  }, []);

  const libWorkoutsEmptyVariant: LibEmptyVariant = (() => {
    if (workouts.length === 0) return 'true-empty';
    if (filteredAndSortedWorkouts.length === 0 && libWorkoutsHasActiveControls) return 'no-match';
    return null;
  })();

  const libMuscleFilterOptions = useMemo(
    () =>
      libMuscleOptions.map<LibFilterOption>((opt) => ({
        id: opt,
        label: opt,
        matchCount: libMuscleMatchCounts.get(opt) ?? 0,
      })),
    [libMuscleOptions, libMuscleMatchCounts],
  );

  const libEquipmentFilterOptions = useMemo(
    () =>
      libEquipmentOptions.map<LibFilterOption>((opt) => ({
        id: opt,
        label: opt,
        matchCount: libEquipmentMatchCounts.get(opt) ?? 0,
      })),
    [libEquipmentOptions, libEquipmentMatchCounts],
  );

  return {
    workoutSortColumn,
    setWorkoutSortColumn,
    workoutSortDirection,
    setWorkoutSortDirection,
    workoutNameFilter,
    setWorkoutNameFilter,
    workoutDateFilter,
    setWorkoutDateFilter,
    workoutDateFilterType,
    setWorkoutDateFilterType,
    workoutDateFilterEnd,
    setWorkoutDateFilterEnd,
    libMuscleFilter,
    setLibMuscleFilter,
    libEquipmentFilter,
    setLibEquipmentFilter,
    libDurationFilter,
    setLibDurationFilter,
    libWorkoutsPage,
    setLibWorkoutsPage,
    libOpenChip,
    setLibOpenChip,
    libMuscleChipRef,
    libEquipmentChipRef,
    libDurationChipRef,
    libSortChipRef,
    handleWorkoutSort,
    filteredAndSortedWorkouts,
    libMuscleOptions,
    libEquipmentOptions,
    libCurrentSort,
    libSortIsDefault,
    libDurationActive,
    libMuscleMatchCounts,
    libEquipmentMatchCounts,
    libDurationMatchCounts,
    libWorkoutsHasActiveControls,
    libWorkoutsAppliedCount,
    handleClearAllWorkouts,
    libWorkoutsTotalPages,
    libWorkoutsPageRows,
    libRangeStart,
    libRangeEnd,
    libBuildPageWindow,
    libWorkoutsEmptyVariant,
    libMuscleFilterOptions,
    libEquipmentFilterOptions,
    LIB_SORT_OPTIONS,
  };
}

export type LibraryWorkoutsApi = ReturnType<typeof useLibraryWorkouts>;
